// components/VoiceCard.jsx — Anamoria SPA
// v2.16 — NEW-163 Increment B (2026-10-30): (a) NEW-164 SUPERSEDED — the footer MediaTypeChip is
//   removed (the chip now lives in the response-card header, GroupMemoryCard); the spaceIsGroup
//   prop is kept (plumbing only, inert). Non-group spaces were already chip-free → byte-identical.
//   (b) additive `headerSlot` / `footerSlot` / `footerEndSlot` props (default null): header slot first
//   inside the card chrome; footer slot replaces the date attribution; footer-end slot sits just before the
//   privacy icon — so MemoryFeed v2.47 can place the response-card header, "Today" and the type chip inside a member's voice card.
// v2.15 — NEW-164 (2026-10-25): group-feed media-type chip in the footer (Voice), gated on
//   the new spaceIsGroup prop; presentational, no behaviour change.
// v2.14 — NEW-105 (2026-09-26): footer prefers the moment day (dayLabel = memory.dayText?.trim() || date).
// v2.13 — DF-7 feed chrome: favorite heart glyph 16 -> 20px (member overlay only).
// v2.12 — feed_redesign_v2: additive `redesign` prop → .cardV2 bordered chrome
//   when set (feed, non-embedded). Falsy everywhere else ⇒ byte-identical;
//   MomentDetailPage + embedded-in-moment unaffected. (July 27, 2026)
// v2.11 — Additive `embedded` prop for MomentCard presence slot (July 20, 2026)
//
// Changes from v2.10 (3.1; momentcard-3-1-DRAFT-for-review-v0.4.md §2, D-3.14/F-1):
//   - NEW prop `embedded = false`. When true (only MomentCard passes it):
//     favorite/edit overlay, own title, own footer, and root button semantics
//     (onClick/role/tabIndex) are conditionally NOT rendered — no ghost
//     controls, no duplicated chrome inside a moment card. Theme, accents,
//     header chrome, player, waveform, duration all kept.
//   - Legacy call sites pass nothing ⇒ default false ⇒ byte-identical render.
//   - CSS: one appended class .cardEmbedded (cursor default).
//
// v2.10 — Contributor name attribution in footer (June 27, 2026)
//
// Changes from v2.9:
//   - Footer date span now shows "Name · date" for contributor memories
//     (creatorType === 'contributor' && creatorName). Owner memories show
//     plain date as before. creatorName sourced from anamoria-memories v2.14
//     (LEFT JOIN contributors in feed query).
//   - Zero other changes. Playback, waveform, themes, overlay — all
//     byte-identical to v2.9.
//
// v2.9 — Smooth playback sweep + spacious bar density (May 26, 2026)
//
// Changes from v2.8:
//   - ISSUE 1 FIX: Replaced ontimeupdate (~4Hz) with requestAnimationFrame
//     (~60fps) for waveform progress tracking. Progress sweep now animates
//     smoothly from the first frame of playback, including on 1-second
//     recordings. Pattern ported from RecordPage v5.8 (proven in production).
//   - ISSUE 2 FIX: Reduced bar count from 36 to 20, increased bar width
//     from 3px to 4px, increased gap from 2.5px to 4px, increased minimum
//     bar height from 2px to 3px. Produces a spacious, readable waveform
//     matching the visual feel preferred by the product owner.
//   - Added rafRef for animation frame cleanup on unmount.
//   - All other logic unchanged: sqrt gain, clipPath reveal, fallback
//     12-bar HTML, theme conditionals, preload, overlay, icons.
//
// v2.8 — Waveform sizing fix (May 23, 2026)
//   - Increased waveform height from 20px to 28px.
//   - Bar count 36, bar width 3px, gap 2.5px.
//
// v2.7 — WhatsApp-style discrete bars (May 3, 2026)
//   - 48 bars, 2px wide, 2px gap, pill-shaped tips via rx=1.
//   - Sqrt gain curve, two <g> groups (base + progress), clipPath reveal.
//
// v2.6 — Filled ribbon shape (rejected — read as decoration, not waveform).
// v2.5 — Visual polish on stroke-based path rendering (rejected — too dense at 80 bars).
// v2.4 — Real waveform SVG path rendering for warm + sage themes.
// v2.3 — UI polish: warm theme label "VOICE MEMORY" → "VOICE" (April 29, 2026).
// v2.2 — Inline signed URL support (April 22, 2026).
// v2.1 — 4-theme voice card system (April 21, 2026).
// v2.0 — Space screen overhaul (April 1, 2026).
//
// API fields (camelCase from memories handler):
//   memory.voiceNote.s3Key, memory.voiceNote.duration, memory.voiceNote.playbackUrl
//   memory.voiceNote.waveform.peaks (array of 80 amplitudes 0-1, written by postprocess Lambda)
//   memory.createdAt, memory.isPrivate, memory.isFavorite, memory.s3Key

import { useState, useRef, useCallback, useEffect } from 'react';
import styles from './VoiceCard.module.css';

/* ─── Helpers ─── */

function formatDuration(val) {
  const s = Math.round(Number(val) || 0);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

// Waveform bar heights — fallback for pre-feature voice notes (no peaks data)
const WAVE_BARS = [8, 14, 10, 18, 12, 20, 8, 16, 14, 10, 18, 6];

/* ─── Heart SVG ─── */

function HeartIcon({ filled }) {
  if (filled) {
    return (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

/* ─── Edit (pencil) SVG ─── */

function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  );
}

/* ─── Privacy SVG icons ─── */

function PrivacyIcon({ isPrivate }) {
  if (isPrivate) {
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#737373" strokeWidth="1.5" strokeLinecap="round">
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
    );
  }
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#737373" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

/* ═══════════════════════════════════════
   VoiceCard Component
   ═══════════════════════════════════════ */

export default function VoiceCard({ memory, getApi, theme = 'warm', onFavorite, onEdit, onClick, embedded = false, redesign = false, spaceIsGroup = false, headerSlot = null, footerSlot = null, footerEndSlot = null }) {
  const [playing, setPlaying] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const rafRef = useRef(null);       // v2.9: rAF handle for smooth progress

  const s3Key = memory.voiceNote?.s3Key || memory.s3Key;
  const duration = memory.voiceNote?.duration || 0;
  const title = memory.title || 'Voice Note';
  const isPrivate = memory.isPrivate;
  const isFavorite = memory.isFavorite;
  const createdAt = memory.createdAt;
  const dayLabel = memory.dayText?.trim() || formatDate(createdAt);

  // v2.10: Contributor name attribution
  const attribution = (memory.creatorType === 'contributor' && memory.creatorName)
    ? `${memory.creatorName} \u00b7 ${dayLabel}`
    : dayLabel;

  const themeClass = styles[`theme_${theme}`] || styles.theme_warm;

  const isWarm = theme === 'warm';
  const isStory = theme === 'story';
  const isSage = theme === 'sage';
  const isClean = theme === 'clean';
  const usesWaveform = isWarm || isSage;
  const usesProgress = isStory || isClean;

  const peaks = memory.voiceNote?.waveform?.peaks;
  const hasRealWaveform = Array.isArray(peaks) && peaks.length > 0;

  /* ─── v2.9: rAF-based progress tracking ─── */

  const startProgressLoop = useCallback(() => {
    const tick = () => {
      const audio = audioRef.current;
      if (audio && audio.duration && !audio.paused) {
        setProgress((audio.currentTime / audio.duration) * 100);
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const stopProgressLoop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  /* ─── Audio preload + playback ─── */

  // v2.8: Preload audio when playbackUrl is available so first play is
  // instant. Without this, tapping play triggers Audio construction +
  // network fetch, causing a visible delay.
  // v2.9: Removed ontimeupdate; progress driven by rAF loop instead.
  useEffect(() => {
    const playbackUrl = memory.voiceNote?.playbackUrl;
    if (!playbackUrl || audioRef.current) return;
    const audio = new Audio();
    audio.preload = 'auto';
    audio.src = playbackUrl;
    audio.onended = () => { stopProgressLoop(); setPlaying(false); setProgress(0); };
    audio.onerror = () => { stopProgressLoop(); setPlaying(false); setLoadingAudio(false); };
    audioRef.current = audio;
    return () => {
      stopProgressLoop();
      audio.pause();
      audio.onended = null;
      audio.onerror = null;
    };
  }, [memory.voiceNote?.playbackUrl, stopProgressLoop]);

  // v2.9: Clean up rAF on unmount
  useEffect(() => {
    return () => stopProgressLoop();
  }, [stopProgressLoop]);

  const handlePlayPause = useCallback(async (e) => {
    e.stopPropagation();
    if (loadingAudio) return;

    if (audioRef.current && playing) {
      audioRef.current.pause();
      stopProgressLoop();
      setPlaying(false);
      return;
    }
    if (audioRef.current && !playing) {
      audioRef.current.play();
      setPlaying(true);
      startProgressLoop();
      return;
    }
    if (!s3Key) return;

    // Fallback: if preload didn't fire (no inline playbackUrl), fetch now
    setLoadingAudio(true);
    try {
      const api = getApi();
      const data = await api.get(`/media/playback/${encodeURIComponent(s3Key)}`);
      const playbackUrl = data.playbackUrl;
      const audio = new Audio(playbackUrl);
      audioRef.current = audio;
      audio.onended = () => { stopProgressLoop(); setPlaying(false); setProgress(0); };
      audio.onerror = () => { stopProgressLoop(); setPlaying(false); setLoadingAudio(false); };
      await audio.play();
      setPlaying(true);
      startProgressLoop();
    } catch (err) {
      console.error('Playback error:', err);
    } finally {
      setLoadingAudio(false);
    }
  }, [playing, loadingAudio, s3Key, getApi, startProgressLoop, stopProgressLoop]);

  const handleFavorite = useCallback((e) => {
    e.stopPropagation();
    if (onFavorite) onFavorite(memory);
  }, [memory, onFavorite]);

  const handleEdit = useCallback((e) => {
    e.stopPropagation();
    if (onEdit) onEdit(memory);
  }, [memory, onEdit]);

  const handleCardClick = useCallback(() => {
    if (onClick) onClick(memory);
  }, [memory, onClick]);

  const renderPlayIcon = () => {
    if (loadingAudio) {
      return <div className={styles.playLoading} />;
    }
    if (playing) {
      return (
        <svg viewBox="0 0 24 24" width="10" height="10">
          <rect x="5" y="4" width="4" height="16" rx="1" fill="white" />
          <rect x="15" y="4" width="4" height="16" rx="1" fill="white" />
        </svg>
      );
    }
    return (
      <svg viewBox="0 0 24 24" width="9" height="9">
        <path d="M6 4l14 8-14 8V4z" fill="white" />
      </svg>
    );
  };

  return (
    <div
      className={`${styles.card} ${themeClass}${embedded ? ` ${styles.cardEmbedded}` : ''}${redesign && !embedded ? ` ${styles.cardV2}` : ''}${redesign && embedded ? ` ${styles.embeddedV2}` : ''}`}
      onClick={embedded ? undefined : handleCardClick}
      role={embedded ? undefined : 'button'}
      tabIndex={embedded ? undefined : 0}
    >
      {(isStory || isClean) && (
        <div className={styles.accent} />
      )}
      {headerSlot}

      <div className={styles.header}>
        <div className={styles.headerTop}>
          {isWarm && <span className={styles.dot} />}
          {isWarm && <span className={styles.label}>VOICE</span>}
          {isStory && <span className={styles.pauseIcon}>❙❙</span>}
          {isSage && <span className={styles.micIcon}>🎙</span>}
        </div>
        {!embedded && <p className={styles.title}>{title}</p>}
      </div>

      <div className={styles.player}>
        <button
          className={styles.playBtn}
          onClick={handlePlayPause}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {renderPlayIcon()}
        </button>

        {/* v2.9: 20 bars, 4px wide, 4px gap, 28px height — spacious layout */}
        {usesWaveform && hasRealWaveform && (() => {
          const TARGET = 20;
          const BAR_W = 4;
          const GAP = 4;
          const STEP = BAR_W + GAP;
          const W = TARGET * STEP - GAP;
          const H = 28;
          const MIN_H = 3;
          const bucket = peaks.length / TARGET;
          const bars = [];
          for (let i = 0; i < TARGET; i++) {
            const start = Math.floor(i * bucket);
            const end = Math.floor((i + 1) * bucket);
            let max = 0;
            for (let j = start; j < end && j < peaks.length; j++) {
              if (peaks[j] > max) max = peaks[j];
            }
            const clamped = Math.max(0, Math.min(1, max));
            const gained = Math.sqrt(clamped);
            const h = Math.max(MIN_H, gained * H);
            const x = i * STEP;
            const y = (H - h) / 2;
            bars.push({ x, y, h });
          }
          const clipId = `wf-clip-${memory.id}`;
          return (
            <svg
              viewBox={`0 0 ${W} ${H}`}
              preserveAspectRatio="none"
              className={styles.waveformSvg}
              aria-hidden="true"
            >
              <defs>
                <clipPath id={clipId}>
                  <rect x="0" y="0" width={(progress / 100) * W} height={H} />
                </clipPath>
              </defs>
              <g className={styles.waveformBase}>
                {bars.map((b, i) => (
                  <rect key={i} x={b.x} y={b.y} width={BAR_W} height={b.h} rx={BAR_W / 2} />
                ))}
              </g>
              <g className={styles.waveformProgress} clipPath={`url(#${clipId})`}>
                {bars.map((b, i) => (
                  <rect key={i} x={b.x} y={b.y} width={BAR_W} height={b.h} rx={BAR_W / 2} />
                ))}
              </g>
            </svg>
          );
        })()}

        {/* Fallback: 12-bar HTML when peaks absent (pre-feature memos) */}
        {usesWaveform && !hasRealWaveform && (
          <div className={styles.waveform}>
            {WAVE_BARS.map((h, i) => (
              <div
                key={i}
                className={styles.waveBar}
                style={{
                  height: `${h}px`,
                  opacity: playing ? 0.5 : 0.3,
                }}
              />
            ))}
          </div>
        )}

        {usesProgress && (
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {isClean ? (
          <span className={styles.durationPill}>{formatDuration(duration)}</span>
        ) : (
          <span className={styles.duration}>{formatDuration(duration)}</span>
        )}
      </div>

      {!embedded && (
        <div className={styles.footer}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {footerSlot || <span className={styles.date}>{attribution}</span>}   {/* v2.16: a response card's footer carries the gentle day here, the chip at the end */}
          </span>
          <span className={styles.privacyIcon} style={footerEndSlot ? { display: 'inline-flex', alignItems: 'center', gap: 6 } : undefined}>
            {footerEndSlot}
            <PrivacyIcon isPrivate={isPrivate} />
          </span>
        </div>
      )}

      {!embedded && (
        <div className={`${styles.overlay} ${isFavorite ? styles.overlayLiked : ''}`}>
          <button
            className={`${styles.overlayBtn} ${isFavorite ? styles.overlayBtnLiked : ''}`}
            onClick={handleFavorite}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-pressed={isFavorite}
          >
            <HeartIcon filled={isFavorite} />
          </button>
          <button
            className={`${styles.overlayBtn} ${styles.overlayEdit} ${isFavorite ? styles.overlayEditWhenLiked : ''}`}
            onClick={handleEdit}
            aria-label="Edit memory"
          >
            <EditIcon />
          </button>
        </div>
      )}
    </div>
  );
}
